import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, ArrowRight, History, RotateCcw } from 'lucide-react';
import { parseMaintenanceInput } from '../utils/maintenanceParser';
import { ParsedMaintenanceIntent } from '../utils/maintenanceParser';

// 🛰️ Strict Interface for the AI Parser Output
interface ParsedMaintenance {
  title: string;
  isHistorical: boolean;
  cost?: number;
  provider?: string;
  recurrence?: {
    interval: number;
    frequency: 'days' | 'weeks' | 'months' | 'years';
  };
  date?: string;
}

interface NLPMaintenanceInputProps {
  // 🛰️ Typed callback to match the Interpreted Intent
  onConfirm: (data: ParsedMaintenanceIntent) => void;
}

export const NLPMaintenanceInput: React.FC<NLPMaintenanceInputProps> = ({ onConfirm }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [parsedData, setParsedData] = useState<ParsedMaintenanceIntent | null>(null);

  /**
   * 🧠 HEURISTIC INTERPRETATION
   * Triggers the parser when the query length indicates a full sentence.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.length > 10) {
        const result = parseMaintenanceInput(query);
        setParsedData(result as ParsedMaintenanceIntent); // 🎯 CHANGE `ParsedMaintenance` to `ParsedMaintenanceIntent`
      } else {
        setParsedData(null);
      }
    }, 300); // Debounce to prevent parser thrashing

    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className="w-full space-y-4">
      <div className="relative group">
        <div className="absolute left-6 top-1/2 -translate-y-1/2">
          <Sparkles className={`w-6 h-6 transition-colors duration-500 ${query ? 'text-brand animate-pulse' : 'text-slate-300'}`} />
        </div>
        <input
          type="text"
          value={query}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          placeholder={t('maint.nlp_placeholder', 'Tell the engine what happened or what is planned...')}
          className="w-full pl-16 pr-20 py-6 bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-xl border-2 border-transparent focus:border-brand outline-none font-bold text-lg text-slate-900 dark:text-white transition-all placeholder:text-slate-300 dark:placeholder:text-slate-700"
        />
        <button 
          onClick={() => parsedData && onConfirm(parsedData)}
          disabled={!parsedData}
          className={`absolute right-3 top-1/2 -translate-y-1/2 p-4 rounded-full transition-all duration-300 ${
            parsedData 
              ? 'bg-brand text-white scale-100 shadow-lg shadow-brand/40' 
              : 'bg-slate-100 text-slate-300 scale-90 opacity-0 pointer-events-none'
          }`}
        >
          <ArrowRight className="w-6 h-6" />
        </button>
      </div>

      {/* --- 🛰️ PREVIEW VALIDATION CARD --- */}
      {parsedData && (
        <div className="bg-white dark:bg-slate-900 border border-indigo-100 dark:border-indigo-900/50 p-6 rounded-[2.5rem] shadow-sm animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="flex justify-between items-start mb-6">
            <div className="space-y-1">
              <span className="text-2xs font-black uppercase text-brand tracking-[0.2em] italic">
                {t('ai.interpreted_intent', 'Cognitive Interpretation')}
              </span>
              <h4 className="font-black text-xl text-slate-900 dark:text-white uppercase italic tracking-tighter">
                {parsedData.title}
              </h4>
            </div>

            {parsedData.isHistorical ? (
              <div className="flex items-center gap-2 text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest border border-amber-100 dark:border-amber-800">
                <History className="w-4 h-4" /> {t('maint.historical_log', 'Registry Entry')}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-brand bg-indigo-50 dark:bg-indigo-900/20 px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest border border-indigo-100 dark:border-indigo-800">
                <RotateCcw className="w-4 h-4" /> {t('maint.planned_recurrence', 'New Rhythm')}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 pt-4 border-t border-slate-50 dark:border-slate-800/50">
            {parsedData.cost && (
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('maint.cost', 'Expenditure')}</p>
                <p className="font-bold text-slate-800 dark:text-white text-lg">${parsedData.cost?.toLocaleString()}</p>
              </div>
            )}
            {parsedData.recurrence?.frequency && (
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('maint.frequency', 'Interval')}</p>
                <p className="font-bold text-slate-800 dark:text-white text-lg uppercase italic">
                  Every {parsedData.recurrence?.interval} {parsedData.recurrence?.frequency}
                </p>
              </div>
            )}
            {parsedData.provider && (
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('maint.provider', 'Entity')}</p>
                <p className="font-bold text-slate-800 dark:text-white text-lg uppercase truncate">{parsedData.provider}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};